import getEnvVar from '@/utils/getEnvVar';
import axios, { AxiosResponse } from 'axios';
import { retry } from './retry';
import { TranslatedQuery } from '@/types/download/TranslatedQuery';
import { MergeValues } from '@/types/download/DownloadButtonProps';
import { validateDownloadQuery } from './validateDownloadQuery';

interface DownloadDataParams {
  bearerToken: string;
  fileNamePrefix: string;
  translatedQuery: TranslatedQuery;
  mergeValues?: MergeValues;
}

async function getDownloadUrl(downloadDataParams: DownloadDataParams): Promise<string | undefined> {
  const { bearerToken, translatedQuery, fileNamePrefix, mergeValues } = downloadDataParams;

  validateDownloadQuery(translatedQuery.sql);

  const devEndpoint = getEnvVar('VITE_DATA_API_URL');

  const response: AxiosResponse = await axios.post(
    `${devEndpoint}/download/data`,
    {
      query: translatedQuery.sql,
      values: translatedQuery.values,
      token: bearerToken,
      fileNamePrefix,
      ...(mergeValues ? { mergeValues } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearerToken}`,
      },
    },
  );

  if (response.status !== 200) {
    throw new Error(`Unexpected response status: ${response.status}`);
  }
  return response.data?.file_signed_url;
}

export const downloadData = async (downloadDataParams: DownloadDataParams): Promise<string | undefined> => {
  const maxRetries = 3;
  try {
    return await retry(() => getDownloadUrl(downloadDataParams), maxRetries);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `API request failed after ${maxRetries} attempts. Status: ${error.response.status}. ${error.message}`,
      );
    }
    throw new Error(
      `API request failed after ${maxRetries} attempts. ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};
